import { useTheme } from "next-themes";
import { useNavigate } from "react-router-dom";
import { currentPlatform, ePath, loginHref } from "@/utils/paths";
import { Sun, Moon, Globe, LogOut, User, ChevronDown, Search, Command } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAutoRoleAuth } from "@/hooks/useAuthHooks";
import { useEntrepreneurAccesses } from "@/hooks/useEntrepreneurAccesses";
import { useCommandPaletteStore } from "@/stores/commandPaletteStore";
import { useTour } from "@/hooks/useTour";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { isStaffRole } from "@/types/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NotificationBell } from "@/components/notifications/NotificationBell";

export const TopNavbar = () => {
  const { theme, setTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, logout, isLoading: isLoggingOut } = useAutoRoleAuth();
  const navigate = useNavigate();
  const openCommandPalette = useCommandPaletteStore((state) => state.open);
  useTour();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
  };

  const getUserDisplayName = () => {
    if (!user) return 'Guest';
    return user.profile_data?.first_name ||
           user.profile_data?.full_name?.split(' ')[0] ||
           user.username;
  };

  const isEntrepreneur = user?.role === 'ENTREPRENEUR';
  const platform = currentPlatform();
  // Only an entrepreneur has B2C memberships, and only they need the query.
  const { getAccess } = useEntrepreneurAccesses(isEntrepreneur);

  const handleProfile = () => {
    // The role decides, not the URL: `/me` is the staff page mounted at the
    // host root, and a staff user on a platform-less path was landing in the
    // founders' portal.
    navigate(isEntrepreneur ? ePath('/me') : '/me');
  };

  const isFoundersHub = platform === 'foundershub';
  const searchPlaceholder = isFoundersHub ? "Search entrepreneurs..." : "Search...";
  // `entrepreneur_state` is the Founder's Hub FSM and nothing else: a Gaia
  // entrepreneur sits at PENDING_LINKEDIN_PARSE forever, so gating the navbar
  // on APPROVED hid the notification bell from every one of them. Their real
  // membership is the platform access row, which is exactly what
  // `GaiaParticipantRoute` reads to let them in.
  const isApprovedEntrepreneur = isEntrepreneur && (user as any)?.entrepreneur_state === 'APPROVED';
  const hasActivePlatformAccess =
    isEntrepreneur &&
    platform !== null &&
    platform !== 'terminus' &&
    getAccess(platform)?.is_active === true;
  const showFullNavbar = isStaffRole(user?.role) || isApprovedEntrepreneur || hasActivePlatformAccess;

  return (
    <header className="h-14 border-b bg-background sticky top-0 z-50">
      <div className="flex h-full items-center justify-between px-6">
        {/* Left side - Search (hidden for entrepreneurs) */}
        <div className="flex items-center gap-4">
          {!isEntrepreneur && (
            <button
              onClick={openCommandPalette}
              data-tour-id="topnav-search"
              className="flex items-center gap-2 h-9 px-3 text-sm text-muted-foreground border rounded-md hover:bg-muted/50 transition-colors w-64"
            >
              <Search className="h-4 w-4" />
              <span className="flex-1 text-left">{searchPlaceholder}</span>
              <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
                <Command className="h-3 w-3" />K
              </kbd>
            </button>
          )}
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-1">
          {/* Theme Toggle - hidden for unapproved entrepreneurs */}
          {showFullNavbar && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Moon className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          )}

          {/* Language Toggle */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-9 w-9 p-0">
                <Globe className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-32">
              <DropdownMenuItem
                onClick={() => changeLanguage('en')}
                className={`text-xs ${i18n.language === 'en' ? 'bg-muted' : ''}`}
              >
                English
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => changeLanguage('es')}
                className={`text-xs ${i18n.language === 'es' ? 'bg-muted' : ''}`}
              >
                Español
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => changeLanguage('zh')}
                className={`text-xs ${i18n.language === 'zh' ? 'bg-muted' : ''}`}
              >
                中文
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Notification Bell - hidden for unapproved entrepreneurs */}
          {isAuthenticated && showFullNavbar && <NotificationBell />}

          <div className="h-6 w-px bg-border mx-2" />

          {/* User Menu */}
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-9 gap-2 px-2">
                  <UserAvatar
                    name={getUserDisplayName()}
                    picture={user?.profile_data?.profile_picture || (user?.profile_data as any)?.profilePicture}
                    size="sm"
                  />
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-medium leading-none">
                      {getUserDisplayName()}
                    </span>
                    <span className="text-[10px] text-muted-foreground capitalize">
                      {user?.role?.toLowerCase()}
                    </span>
                  </div>
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                  {user?.email || getUserDisplayName()}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleProfile} className="text-xs">
                  <User className="h-3.5 w-3.5 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  disabled={isLoggingOut}
                  className="text-xs text-red-600 focus:text-red-600"
                >
                  <LogOut className="h-3.5 w-3.5 mr-2" />
                  {isLoggingOut ? 'Signing out...' : 'Sign Out'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => navigate(loginHref())}>
              Sign In
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};
